import * as Sentry from '@sentry/react';
import { env } from '@/shared/config/env';

const SENSITIVE_QUERY_KEYS = ['token', 'access_token', 'code', 'email', 'password', 'secret'];

function shouldDropEvent(hint: Sentry.EventHint): boolean {
  if (
    hint.originalException instanceof Error &&
    '__sentryHandled' in hint.originalException &&
    (hint.originalException as Error & Record<string, boolean>)['__sentryHandled'] === true
  ) {
    return true;
  }

  return false;
}

function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;

  try {
    const parsed = new URL(url);
    for (const key of SENSITIVE_QUERY_KEYS) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, '[redacted]');
      }
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

export function initSentry(): void {
  if (!env.PROD) {
    return;
  }

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.MODE,
    release: env.VITE_APP_VERSION,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: false,
        maskAllInputs: true,
      }),
    ],
    beforeSend(event, hint) {
      if (shouldDropEvent(hint)) {
        return null;
      }

      if (event.request?.url) {
        event.request.url = scrubUrl(event.request.url) ?? event.request.url;
      }

      return event;
    },
  });
}

export function markAsHandled(error: Error): void {
  (error as Error & Record<string, boolean>)['__sentryHandled'] = true;
}
